// frontend/src/components/OpportunityAccessModal.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-01-14
// Updated:      2026-09-16
//
// Description:
//   Opportunity access/share modal.
//
// Fixes (2026-01-21):
//   • Uses canonical shared Axios instance: ../api/axiosInstance
//   • Admin override is SELF-SUFFICIENT: fetches current user role via API on open.
//   • Resolves resource_id to full names by loading /api/resources.
//   • Filters shareable resources to: is_sales=true AND active=true (per requirement).
//   • Per-row revoke reason (no shared input state across rows).
//   • Robust share/revoke UX: busy state + surfaced errors + awaits refresh.
//   • Owner protection UX: disables revoke for owner (backend already enforces).
//
// Responsive pass (2026-09-16):
//   • Modal is viewport-safe and scrollable on short/narrow screens.
//   • Share controls collapse from the desktop three-column layout to mobile rows.
//   • Access-row table is retained on desktop; mobile uses one complete card per record.
//   • Long opportunity/resource/error text wraps without forcing horizontal overflow.
//   • No permission, admin override, resource filtering, share/revoke, owner, or refresh logic changed.

import React, { useEffect, useMemo, useState } from "react";
import { useTheme } from "../context/ThemeContext.jsx";
import axiosInstance from "../api/axiosInstance";

const ADMIN_OVERRIDE_ROLES = new Set(["Admin", "System Admin", "Super Admin"]);

function safeStr(v) {
  return v == null ? "" : String(v);
}

function resourceDisplayName(r) {
  if (!r) return "";
  const first = safeStr(r.first_name || r.firstname || r.forename).trim();
  const last = safeStr(r.last_name || r.lastname || r.surname).trim();
  const full = `${first} ${last}`.trim();
  return (
    full ||
    safeStr(r.full_name).trim() ||
    safeStr(r.name).trim() ||
    safeStr(r.display_name).trim() ||
    safeStr(r.email).trim() ||
    (r.id != null ? `Resource ${r.id}` : "")
  );
}

function isSalesResource(r) {
  // Support common naming variants without guessing schema changes.
  const v = r?.is_sales ?? r?.isSales ?? r?.sales ?? r?.sales_user ?? r?.is_sales_user;
  return v === true;
}

function isActiveResource(r) {
  // Support common naming variants.
  const v = r?.is_active ?? r?.isActive ?? r?.active ?? r?.enabled;
  return v === true;
}

async function fetchCurrentUser() {
  // Try common endpoints without hard-coding one that may differ between modules.
  const candidates = ["/api/users/me", "/api/users/current", "/api/users/profile", "/api/auth/me"];
  let lastErr = null;

  for (const url of candidates) {
    try {
      const res = await axiosInstance.get(url);
      if (res?.data) return res.data;
    } catch (e) {
      lastErr = e;
    }
  }

  if (lastErr) throw lastErr;
  return null;
}

export default function OpportunityAccessModal({
  open,
  onClose,
  opportunity,
  accessRows = [],
  loading = false,
  error = "",
  canEdit = false,
  onShare,
  onRevoke,
  onRefresh,

  // Optional: parent can pass these, but modal no longer depends on them.
  currentUserRole = "",
  currentUser = null,
}) {
  const { theme } = useTheme();

  // Share controls
  const [resourceId, setResourceId] = useState("");
  const [resourcePick, setResourcePick] = useState("");
  const [canEditFlag, setCanEditFlag] = useState(false);

  // Resource directory for name resolution
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [resourcesError, setResourcesError] = useState("");
  const [resources, setResources] = useState([]);

  // Current user resolution (for Admin override)
  const [meLoading, setMeLoading] = useState(false);
  const [meError, setMeError] = useState("");
  const [me, setMe] = useState(null);

  // Action state
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState("");

  // Per-row revoke reason (keyed by resource_id)
  const [revokeReasons, setRevokeReasons] = useState({});

  const rows = Array.isArray(accessRows) ? accessRows : [];

  const resolvedRole = useMemo(() => {
    return (
      safeStr(currentUserRole).trim() ||
      safeStr(currentUser?.role).trim() ||
      safeStr(me?.role).trim()
    );
  }, [currentUserRole, currentUser, me]);

  const isAdminOverride = useMemo(() => ADMIN_OVERRIDE_ROLES.has(resolvedRole), [resolvedRole]);

  const effectiveCanManageAccess = !!canEdit || isAdminOverride;

  const overlay = useMemo(
    () => ({
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.45)",
      display: open ? "flex" : "none",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999,
      padding: 16,
    }),
    [open]
  );

  const modal = useMemo(
    () => ({
      width: "min(980px, 100%)",
      maxHeight: "calc(100dvh - 32px)",
      overflowY: "auto",
      overflowX: "hidden",
      boxSizing: "border-box",
      background: theme?.card_bg || "#fff",
      borderRadius: 16,
      padding: 16,
      boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
      border: theme?.card_border || "1px solid rgba(0,0,0,0.10)",
      display: "flex",
      flexDirection: "column",
      gap: 12,
      fontFamily: theme?.font_body || "inherit",
      WebkitOverflowScrolling: "touch",
      overscrollBehavior: "contain",
    }),
    [theme]
  );

  const input = {
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    borderRadius: 10,
    border: "1px solid rgba(0,0,0,0.12)",
    padding: "10px 12px",
    fontSize: 12,
    outline: "none",
  };

  const select = {
    ...input,
    padding: "10px 10px",
    background: "#fff",
  };

  const resourceMap = useMemo(() => {
    const m = new Map();
    for (const r of resources || []) {
      if (r && r.id != null) m.set(Number(r.id), r);
    }
    return m;
  }, [resources]);

  const ownerResourceId = useMemo(() => {
    const v = opportunity?.owner_resource_id ?? opportunity?.owner?.resource_id ?? opportunity?.owner_id;
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [opportunity]);

  function rowName(rid) {
    const r = resourceMap.get(Number(rid));
    return resourceDisplayName(r);
  }

  // Load current user + resources when modal opens
  useEffect(() => {
    let mounted = true;

    async function loadMe() {
      setMeError("");
      setMeLoading(true);
      try {
        const u = await fetchCurrentUser();
        if (!mounted) return;
        setMe(u || null);
      } catch (e) {
        if (!mounted) return;
        const msg = e?.response?.data?.detail || e?.message || "Failed to resolve current user role.";
        setMeError(String(msg));
      } finally {
        if (mounted) setMeLoading(false);
      }
    }

    async function loadResources() {
      setResourcesError("");
      setResourcesLoading(true);
      try {
        // We request both active/inactive, then filter strictly in UI to active + is_sales.
        // This prevents schema drift and still allows name resolution for existing access rows.
        const res = await axiosInstance.get("/api/resources", {
          params: { include_inactive: true, limit: 500 },
        });
        const data = Array.isArray(res?.data) ? res.data : [];
        if (!mounted) return;
        setResources(data);
      } catch (e) {
        if (!mounted) return;
        const msg =
          e?.response?.data?.detail || e?.message || "Failed to load resources (cannot resolve names).";
        setResourcesError(String(msg));
      } finally {
        if (mounted) setResourcesLoading(false);
      }
    }

    if (open) {
      loadMe();
      loadResources();
    }

    return () => {
      mounted = false;
    };
  }, [open]);

  // Reset inputs when opening
  useEffect(() => {
    if (!open) return;
    setResourceId("");
    setResourcePick("");
    setCanEditFlag(false);
    setRevokeReasons({});
    setResourcesError("");
    setMeError("");
    setActionError("");
    setActionBusy(false);
  }, [open]);

  // Filter resources for the SHARE dropdown: is_sales=true AND active=true
  const shareableResourceOptions = useMemo(() => {
    const list = Array.isArray(resources) ? resources.slice() : [];
    const filtered = list.filter((r) => isSalesResource(r) && isActiveResource(r));
    filtered.sort((a, b) => resourceDisplayName(a).localeCompare(resourceDisplayName(b)));
    return filtered;
  }, [resources]);

  function getShareTargetId() {
    const candidate = resourcePick || resourceId;
    const rid = Number(candidate);
    return Number.isFinite(rid) && rid > 0 ? rid : null;
  }

  async function awaitMaybe(p) {
    // Allows onRefresh to be sync or async.
    try {
      return await Promise.resolve(p);
    } catch {
      return null;
    }
  }

  async function handleShare() {
    const rid = getShareTargetId();
    if (!rid) return;

    setActionError("");
    setActionBusy(true);

    try {
      // If parent didn’t wire onShare, fail loudly (no silent "worked" UI).
      if (!onShare) {
        throw new Error("Share handler (onShare) not wired in parent.");
      }

      await Promise.resolve(onShare(rid, !!canEditFlag));

      // Force a refresh and WAIT for it.
      if (onRefresh) {
        await awaitMaybe(onRefresh());
      }
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.message ||
        "Share failed (check backend logs / route wiring).";
      setActionError(String(msg));
      return;
    } finally {
      setActionBusy(false);
    }

    // Clear form after successful refresh
    setResourceId("");
    setResourcePick("");
    setCanEditFlag(false);
  }

  async function handleRevoke(rid) {
    if (!rid) return;

    // Owner protection UX (backend enforces too)
    if (ownerResourceId && Number(rid) === Number(ownerResourceId)) return;

    setActionError("");
    setActionBusy(true);

    try {
      if (!onRevoke) {
        throw new Error("Revoke handler (onRevoke) not wired in parent.");
      }

      const reason = safeStr(revokeReasons?.[rid]).trim() || "revoked";
      await Promise.resolve(onRevoke(Number(rid), reason));

      setRevokeReasons((prev) => {
        const next = { ...(prev || {}) };
        delete next[rid];
        return next;
      });

      if (onRefresh) {
        await awaitMaybe(onRefresh());
      }
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.message ||
        "Revoke failed (check backend logs / route wiring).";
      setActionError(String(msg));
    } finally {
      setActionBusy(false);
    }
  }

  const topTitle = useMemo(() => {
    const name = opportunity?.name || opportunity?.title || "";
    const id = opportunity?.id ? `Opportunity ${opportunity.id}` : "";
    return name ? `Access · ${name}` : id ? `Access · ${id}` : "Access";
  }, [opportunity]);

  const permissionHint = useMemo(() => {
    if (effectiveCanManageAccess) return "";
    if (resolvedRole) return `Role '${resolvedRole}' has no override and canEdit=false.`;
    if (meLoading) return "Resolving role…";
    return "No permission (role unresolved and canEdit=false).";
  }, [effectiveCanManageAccess, resolvedRole, meLoading]);

  const disabledByBusy = actionBusy || loading;

  return (
    <div style={overlay} onMouseDown={(e) => e.target === e.currentTarget && onClose && onClose()}>
      <div style={modal}>
        <div className="flex min-w-0 items-start justify-between gap-3 sm:items-center">
          <div className="min-w-0 flex-1 break-words" style={{ fontWeight: 900, fontSize: 16 }}>
            <span className="break-words">{topTitle}</span>
            {resolvedRole ? (
              <span className="ml-0 mt-1 block break-words sm:ml-2.5 sm:mt-0 sm:inline" style={{ fontSize: 12, fontWeight: 800, opacity: 0.7 }}>
                ({resolvedRole}
                {isAdminOverride ? " · override" : ""})
              </span>
            ) : null}
          </div>

          <button
            type="button"
            onClick={() => onClose && onClose()}
            style={{
              width: 34,
              height: 34,
              borderRadius: 999,
              border: 0,
              cursor: "pointer",
              background: "rgba(0,0,0,0.08)",
              fontWeight: 900,
              opacity: disabledByBusy ? 0.6 : 1,
              flexShrink: 0,
            }}
            title="Close"
            disabled={disabledByBusy}
          >
            ✕
          </button>
        </div>

        {!effectiveCanManageAccess ? (
          <div className="break-words" style={{ fontSize: 12, opacity: 0.85, color: theme?.danger || "#b91c1c", fontWeight: 800 }}>
            Access management disabled: {permissionHint}
          </div>
        ) : null}

        {meError ? <div className="break-words" style={{ fontSize: 12, color: theme?.danger || "#b91c1c" }}>{meError}</div> : null}
        {resourcesError ? (
          <div className="break-words" style={{ fontSize: 12, color: theme?.danger || "#b91c1c" }}>{resourcesError}</div>
        ) : null}

        {actionError ? (
          <div className="break-words" style={{ fontSize: 12, color: theme?.danger || "#b91c1c", fontWeight: 800 }}>
            {actionError}
          </div>
        ) : null}

        {/* SHARE */}
        <div className="grid min-w-0 grid-cols-1 items-end gap-2.5 lg:grid-cols-[minmax(0,1fr)_220px_180px]">
          <div className="min-w-0">
            <div style={{ fontSize: 12, opacity: 0.75, fontWeight: 800 }}>
              Share with Resource{" "}
              <span style={{ fontWeight: 700, opacity: 0.7 }}>(Sales + Active only)</span>
            </div>

            <div className="grid min-w-0 grid-cols-1 gap-2.5 sm:grid-cols-[minmax(0,1fr)_140px]">
              <select
                style={select}
                value={resourcePick}
                onChange={(e) => setResourcePick(e.target.value)}
                disabled={!effectiveCanManageAccess || disabledByBusy}
                title={!effectiveCanManageAccess ? "No permission" : "Select a resource"}
              >
                <option value="">Select resource…</option>
                {shareableResourceOptions.map((r) => (
                  <option key={`r-${r.id}`} value={String(r.id)}>
                    {resourceDisplayName(r)} (ID {r.id})
                  </option>
                ))}
              </select>

              <input
                style={input}
                value={resourceId}
                onChange={(e) => setResourceId(e.target.value)}
                placeholder="Or type ID"
                disabled={!effectiveCanManageAccess || disabledByBusy}
              />
            </div>

            {resourcesLoading ? (
              <div style={{ marginTop: 6, fontSize: 12, opacity: 0.75 }}>Loading resources…</div>
            ) : null}
          </div>

          <div className="flex items-center gap-2.5 lg:self-end" style={{ paddingBottom: 2 }}>
            <input
              type="checkbox"
              checked={canEditFlag}
              onChange={(e) => setCanEditFlag(e.target.checked)}
              disabled={!effectiveCanManageAccess || disabledByBusy}
            />
            <div style={{ fontSize: 12, fontWeight: 800, opacity: effectiveCanManageAccess ? 1 : 0.6 }}>
              Can edit
            </div>
          </div>

          <button
            type="button"
            className="w-full bg-btnActionNormal text-white hover:bg-btnActionHover active:bg-btnActionClick lg:w-auto"
            style={{
              borderRadius: 10,
              padding: "10px 14px",
              fontWeight: 900,
              opacity: effectiveCanManageAccess && !disabledByBusy ? 1 : 0.5,
            }}
            disabled={!effectiveCanManageAccess || disabledByBusy || !getShareTargetId()}
            onClick={handleShare}
            title={
              !effectiveCanManageAccess
                ? "No permission to grant access"
                : disabledByBusy
                  ? "Busy…"
                  : !getShareTargetId()
                    ? "Select or type a valid Resource ID"
                    : "Grant access"
            }
          >
            {actionBusy ? "Working…" : "Share"}
          </button>
        </div>

        {/* HEADER */}
        <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div style={{ fontWeight: 900, fontSize: 14 }}>Access Rows</div>

          <div className="flex min-w-0 flex-wrap items-center gap-2.5">
            {ownerResourceId ? (
              <div className="min-w-0 break-words" style={{ fontSize: 12, opacity: 0.7, fontWeight: 800 }}>
                Owner:{" "}
                <span style={{ opacity: 0.9 }}>
                  {rowName(ownerResourceId) || `Resource ${ownerResourceId}`} (ID {ownerResourceId})
                </span>
              </div>
            ) : null}

            <button
              type="button"
              className="bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white"
              style={{ borderRadius: 10, padding: "8px 10px", fontWeight: 900, fontSize: 12, opacity: disabledByBusy ? 0.6 : 1 }}
              onClick={() => !disabledByBusy && onRefresh && onRefresh()}
              disabled={disabledByBusy}
            >
              Refresh
            </button>
          </div>
        </div>

        {error ? <div className="break-words" style={{ color: theme?.danger || "#b91c1c", fontSize: 12 }}>{error}</div> : null}
        {loading ? <div style={{ fontSize: 12, opacity: 0.75 }}>Loading…</div> : null}

        {/* MOBILE ACCESS ROWS */}
        <div className="space-y-3 md:hidden">
          {rows.map((r, i) => {
            const rid = r?.resource_id != null ? Number(r.resource_id) : null;
            const isOwner = ownerResourceId && rid && Number(rid) === Number(ownerResourceId);
            const label = rid ? `${rowName(rid) || `Resource ${rid}`} (ID ${rid})` : "";
            const reasonVal = rid ? safeStr(revokeReasons?.[rid]) : "";

            return (
              <div
                key={`mobile-${rid || "r"}-${i}`}
                className="min-w-0 rounded-xl border border-black/10 p-3"
              >
                <div className="min-w-0 break-words text-sm font-black">
                  {label || "Unknown resource"}
                </div>

                {isOwner ? (
                  <div className="mt-1 text-[11px] font-extrabold opacity-70">
                    Owner (cannot revoke)
                  </div>
                ) : null}

                <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                  <div className="opacity-70">Can Edit</div>
                  <div className="font-extrabold">{r?.can_edit ? "Yes" : "No"}</div>

                  <div className="opacity-70">Revoked</div>
                  <div className="font-extrabold">{r?.revoked_at ? "Yes" : "No"}</div>
                </div>

                <div className="mt-3">
                  <label className="mb-1 block text-[11px] font-extrabold opacity-70">
                    Revoke reason
                  </label>
                  <input
                    style={input}
                    value={reasonVal}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (!rid) return;
                      setRevokeReasons((prev) => ({ ...(prev || {}), [rid]: v }));
                    }}
                    placeholder="Optional reason"
                    disabled={!effectiveCanManageAccess || disabledByBusy || isOwner}
                  />
                </div>

                <button
                  type="button"
                  className="mt-2.5 w-full bg-btnActionNormal text-white hover:bg-btnActionHover active:bg-btnActionClick"
                  style={{
                    borderRadius: 10,
                    padding: "9px 10px",
                    fontWeight: 900,
                    fontSize: 12,
                    opacity: effectiveCanManageAccess && !disabledByBusy && !isOwner ? 1 : 0.5,
                  }}
                  disabled={!effectiveCanManageAccess || disabledByBusy || !rid || isOwner}
                  onClick={() => handleRevoke(rid)}
                  title={
                    isOwner
                      ? "Owner cannot be revoked"
                      : !effectiveCanManageAccess
                        ? "No permission to revoke"
                        : disabledByBusy
                          ? "Busy…"
                          : "Revoke access"
                  }
                >
                  {actionBusy ? "Working…" : "Revoke"}
                </button>
              </div>
            );
          })}

          {rows.length === 0 ? (
            <div className="rounded-xl border border-black/10 p-3 text-sm opacity-70">
              No access rows.
            </div>
          ) : null}
        </div>

        {/* TABLE */}
        <div
          className="hidden md:block"
          style={{ overflowX: "auto", overflowY: "auto", maxHeight: "50vh", border: "1px solid rgba(0,0,0,0.08)", borderRadius: 12 }}
        >
          <table style={{ width: "100%", minWidth: 760, borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "rgba(0,0,0,0.04)" }}>
                <th style={{ textAlign: "left", padding: 10 }}>Resource</th>
                <th style={{ textAlign: "left", padding: 10 }}>Can Edit</th>
                <th style={{ textAlign: "left", padding: 10 }}>Revoked</th>
                <th style={{ textAlign: "left", padding: 10 }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((r, i) => {
                const rid = r?.resource_id != null ? Number(r.resource_id) : null;
                const isOwner = ownerResourceId && rid && Number(rid) === Number(ownerResourceId);
                const label = rid ? `${rowName(rid) || `Resource ${rid}`} (ID ${rid})` : "";
                const reasonVal = rid ? safeStr(revokeReasons?.[rid]) : "";

                return (
                  <tr key={`${rid || "r"}-${i}`} style={{ borderTop: "1px solid rgba(0,0,0,0.06)" }}>
                    <td style={{ padding: 10 }}>
                      <div style={{ fontWeight: 900, overflowWrap: "anywhere" }}>{label}</div>
                      {isOwner ? (
                        <div style={{ marginTop: 2, fontSize: 11, opacity: 0.7, fontWeight: 800 }}>
                          Owner (cannot revoke)
                        </div>
                      ) : null}
                    </td>

                    <td style={{ padding: 10 }}>{r?.can_edit ? "Yes" : "No"}</td>
                    <td style={{ padding: 10 }}>{r?.revoked_at ? "Yes" : "No"}</td>

                    <td style={{ padding: 10 }}>
                      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        <input
                          style={{ ...input, width: 220 }}
                          value={reasonVal}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (!rid) return;
                            setRevokeReasons((prev) => ({ ...(prev || {}), [rid]: v }));
                          }}
                          placeholder="Revoke reason (optional)"
                          disabled={!effectiveCanManageAccess || disabledByBusy || isOwner}
                        />

                        <button
                          type="button"
                          className="bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white"
                          style={{
                            borderRadius: 10,
                            padding: "8px 10px",
                            fontWeight: 900,
                            fontSize: 12,
                            opacity: effectiveCanManageAccess && !disabledByBusy && !isOwner ? 1 : 0.5,
                          }}
                          disabled={!effectiveCanManageAccess || disabledByBusy || !rid || isOwner}
                          onClick={() => handleRevoke(rid)}
                          title={
                            isOwner
                              ? "Owner cannot be revoked"
                              : !effectiveCanManageAccess
                                ? "No permission to revoke"
                                : disabledByBusy
                                  ? "Busy…"
                                  : "Revoke access"
                          }
                        >
                          {actionBusy ? "Working…" : "Revoke"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {rows.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ padding: 12, opacity: 0.7 }}>
                    No access rows.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end gap-2.5">
          <button
            type="button"
            className="w-full bg-btnGeneralNormal text-white hover:bg-btnGeneralHover active:bg-btnGeneralClick sm:w-auto"
            style={{ borderRadius: 10, padding: "10px 14px", fontWeight: 900, opacity: disabledByBusy ? 0.6 : 1 }}
            onClick={() => !disabledByBusy && onClose && onClose()}
            disabled={disabledByBusy}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
